import React from 'react';
import { bool, oneOf, string } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { isFavoriteListing, toggleFavoriteListing } from '../../ducks/favoriteListings.duck';
import { HeartIcon } from './ListingActionIcons';

import css from './ListingActions.module.css';

export const FAVORITE_BUTTON_ICON = 'icon';
export const FAVORITE_BUTTON_LABELLED = 'labelled';

/**
 * The Save button: a heart on the listing card, a heart with "Save" / "Saved"
 * on the listing page.
 *
 * Everything it does is in toggleFavoriteListing — including the sign-in
 * prompt for a signed-out visitor and the "saved" pop-up — so the card and the
 * page behave the same.
 *
 * The click is stopped here because on the card this sits inside the card's
 * link. Without it, saving would also open the listing.
 */
const FavoriteListingButton = props => {
  const { className, listingId, variant } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const isSaved = useSelector(state => isFavoriteListing(state, listingId));

  const handleClick = e => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(toggleFavoriteListing(listingId));
  };

  const isLabelled = variant === FAVORITE_BUTTON_LABELLED;
  const ariaLabel = intl.formatMessage({
    id: isSaved ? 'ListingActions.unsaveAriaLabel' : 'ListingActions.saveAriaLabel',
  });

  return (
    <button
      type="button"
      className={classNames(
        isLabelled ? css.labelledButton : css.iconButton,
        { [css.saved]: isSaved },
        className
      )}
      onClick={handleClick}
      aria-pressed={isSaved}
      aria-label={isLabelled ? null : ariaLabel}
      title={isLabelled ? null : ariaLabel}
    >
      <HeartIcon className={isLabelled ? css.labelledIcon : css.heartIcon} filled={isSaved} />
      {isLabelled ? (
        <span className={css.label}>
          {intl.formatMessage({ id: isSaved ? 'ListingActions.saved' : 'ListingActions.save' })}
        </span>
      ) : null}
    </button>
  );
};

FavoriteListingButton.defaultProps = {
  className: null,
  variant: FAVORITE_BUTTON_ICON,
};

FavoriteListingButton.propTypes = {
  className: string,
  listingId: string.isRequired,
  variant: oneOf([FAVORITE_BUTTON_ICON, FAVORITE_BUTTON_LABELLED]),
};

export default FavoriteListingButton;
